import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Calendar, Plus, Trash2, Upload, X } from 'lucide-react';

const TYPE_COLORS = {
  public: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  religious: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  school_specific: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const HolidayCalendar = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', date: '', holiday_type: 'public', optional: false });
  const [submitting, setSubmitting] = useState(false);

  const fetchHolidays = useCallback(async () => {
    try {
      const res = await api.get(`/holidays?year=${year}`);
      setHolidays(res.data.data.holidays || []);
    } catch {
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { setLoading(true); fetchHolidays(); }, [fetchHolidays]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.date) return;
    setSubmitting(true);
    try {
      await api.post('/holidays', form);
      toast.success(`Added: ${form.name}`);
      setForm({ name: '', date: '', holiday_type: 'public', optional: false });
      setShowAdd(false);
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add holiday');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await api.delete(`/holidays/${id}`);
      toast.success('Holiday deleted');
      fetchHolidays();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/holidays/bulk', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Uploaded: ${res.data.data.added} added, ${res.data.data.skipped} skipped`);
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
    e.target.value = '';
  };

  // Group by month
  const grouped = holidays.reduce((acc, h) => {
    const m = parseInt(h.date.split('-')[1]) - 1;
    if (!acc[m]) acc[m] = [];
    acc[m].push(h);
    return acc;
  }, {});

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="holidays-loading"><div className="h-8 w-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="holiday-calendar-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center shadow-md shadow-rose-500/20">
            <Calendar className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800" data-testid="holidays-title">Holiday Calendar</h1>
            <p className="text-sm text-slate-500">Manage school holidays. Staff cannot apply leave on these dates.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 rounded-lg border border-slate-200 px-3 text-sm bg-white appearance-none pr-8"
              data-testid="year-select"
            >
              <option value={2025}>2025</option>
              <option value={2026}>2026</option>
              <option value={2027}>2027</option>
            </select>
          </div>
          <label className="h-9 px-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-white text-sm cursor-pointer hover:bg-slate-50 transition-colors" data-testid="upload-csv-btn">
            <Upload className="h-4 w-4 text-slate-500" /> CSV
            <input type="file" accept=".csv" onChange={handleCSV} className="hidden" />
          </label>
          <Button onClick={() => setShowAdd(!showAdd)} className="bg-teal-600 hover:bg-teal-500 text-white rounded-lg h-9 px-4 text-sm" data-testid="add-holiday-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Holiday
          </Button>
        </div>
      </div>

      {/* Add Form */}
      {showAdd && (
        <form onSubmit={handleAdd} className="glass-card p-5 space-y-4" data-testid="add-holiday-form">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Holiday Name</label>
              <input type="text" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Republic Day" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" required data-testid="holiday-name-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm(p => ({ ...p, date: e.target.value }))} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" required data-testid="holiday-date-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Type</label>
              <select value={form.holiday_type} onChange={(e) => setForm(p => ({ ...p, holiday_type: e.target.value }))} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" data-testid="holiday-type-select">
                <option value="public">Public Holiday</option>
                <option value="religious">Religious</option>
                <option value="school_specific">School Specific</option>
              </select>
            </div>
            <div className="flex items-end gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={form.optional} onChange={(e) => setForm(p => ({ ...p, optional: e.target.checked }))} className="rounded" />
                Optional
              </label>
              <Button type="submit" disabled={submitting} className="bg-teal-600 hover:bg-teal-500 text-white rounded-lg h-10 px-5 text-sm" data-testid="save-holiday-btn">
                {submitting ? 'Saving...' : 'Save'}
              </Button>
              <button type="button" onClick={() => setShowAdd(false)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
          </div>
          <p className="text-[11px] text-slate-400">CSV format: name, date (YYYY-MM-DD), type (public/religious/school_specific), optional (true/false)</p>
        </form>
      )}

      {/* Summary */}
      <div className="flex gap-4">
        {['public', 'religious', 'school_specific'].map(type => {
          const count = holidays.filter(h => h.holiday_type === type).length;
          const c = TYPE_COLORS[type];
          return (
            <div key={type} className={`px-4 py-2 rounded-lg ${c.bg} ${c.border} border`}>
              <span className={`text-xs font-bold ${c.text}`}>{count}</span>
              <span className={`text-xs ${c.text} ml-1 capitalize`}>{type.replace('_', ' ')}</span>
            </div>
          );
        })}
        <div className="px-4 py-2 rounded-lg bg-slate-50 border border-slate-200">
          <span className="text-xs font-bold text-slate-700">{holidays.length}</span>
          <span className="text-xs text-slate-500 ml-1">Total</span>
        </div>
      </div>

      {/* Holiday List by Month */}
      {holidays.length === 0 ? (
        <div className="text-center py-16 glass-card">
          <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-400">No holidays added for {year}</p>
          <p className="text-xs text-slate-300 mt-1">Click "Add Holiday" or upload a CSV</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(grouped).sort(([a], [b]) => Number(a) - Number(b)).map(([monthIdx, monthHolidays]) => (
            <div key={monthIdx} className="glass-card overflow-hidden" data-testid={`month-${monthIdx}`}>
              <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-100">
                <h3 className="text-sm font-bold text-slate-700">{MONTHS[monthIdx]} {year}</h3>
              </div>
              <div className="divide-y divide-slate-50">
                {monthHolidays.map(h => {
                  const c = TYPE_COLORS[h.holiday_type] || TYPE_COLORS.public;
                  const d = new Date(h.date + 'T00:00:00');
                  const dayName = d.toLocaleDateString('en', { weekday: 'short' });
                  const dayNum = d.getDate();
                  return (
                    <div key={h.holiday_id} className="flex items-center px-4 py-3 group" data-testid={`holiday-${h.holiday_id}`}>
                      <div className="w-10 h-10 rounded-lg bg-slate-50 flex flex-col items-center justify-center mr-3 flex-shrink-0">
                        <span className="text-sm font-bold text-slate-800 leading-none">{dayNum}</span>
                        <span className="text-[10px] text-slate-400">{dayName}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{h.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${c.bg} ${c.text} capitalize`}>{h.holiday_type.replace('_', ' ')}</span>
                          {h.optional && <span className="text-[10px] text-amber-600 font-medium">Optional</span>}
                        </div>
                      </div>
                      <button onClick={() => handleDelete(h.holiday_id, h.name)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-rose-500 transition-all" data-testid={`delete-holiday-${h.holiday_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HolidayCalendar;
